import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import DocumentService from '../../../../services/DocumentService';
import { useUiContext } from '../../../../context/UiContext';
import downloadDocument from '../../../../helpers/downloadDocument';

export default function DocumentActions({
  doc,
  refetch,
  readOnly,
  isLender,
  lenderId,
}) {
  const { showToast } = useUiContext();

  const navigate = useNavigate();
  const { id } = useParams();

  const download = () => {
    try {
      downloadDocument(doc.id, `${doc.private_document.name}.pdf`);
    } catch (error) {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    }
  };

  const directToEditPage = () => {
    // User is lender
    if (isLender) {
      navigate(`/lenders/${id}/documents/${doc.id}?tab=Document`);
      // User is admin and editing a lender
    } else if (lenderId) {
      navigate(`/lenders/${lenderId}/documents/${doc.id}?tab=Document`);
      // User is admin and editing a loan
    } else {
      navigate(`/loans/${id}/documents/${doc.id}?tab=Document`);
    }
  };

  const { mutate: approveDocument, isPending: isApproving } = useMutation({
    mutationFn: async () => {
      await DocumentService.updateDocument({
        documentId: doc.id,
        formValues: {
          ...doc,
          status: 'accepted',
        },
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Document approved successfully',
        type: 'success',
      });
      refetch?.();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: archiveDocument, isPending: isArchiving } = useMutation({
    mutationFn: async () => {
      await DocumentService.updateDocument({
        documentId: doc.id,
        formValues: {
          ...doc,
          status: 'archived',
        },
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Document archived successfully',
        type: 'success',
      });
      refetch?.();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  return (
    <div className="flex gap-5">
      <button type="button" onClick={download}>
        <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
          <path d="M16 20c.3 0 .5-.1.7-.3l5.7-5.7-1.4-1.4-4 4V8h-2v8.6l-4-4L9.6 14l5.7 5.7c.2.2.4.3.7.3zM9 22h14v2H9z" />
        </svg>
      </button>
      {!readOnly && (
        <>
          <button type="button" onClick={directToEditPage}>
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
            </svg>
          </button>
          {doc.status === 'pending' && (
            <button
              type="button"
              className="btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-emerald-500"
              onClick={approveDocument}
              disabled={isApproving}
            >
              Approve
            </button>
          )}
          <button
            type="button"
            onClick={archiveDocument}
            disabled={isArchiving}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />{' '}
              <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-10" />{' '}
              <path d="M10 12l4 0" />{' '}
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
